/**
 * Nutrition calculation utilities for search results
 */

import {
    OutgoingSearchResult,
    MeasuredPortion,
    PassioFoodDetailResponse,
} from "../types";

/**
 * Recalculates macros for a search result based on a new quantity
 * Assumes the current nutritionPreview represents the current portion
 */
export function recalculateMacros(
    searchResult: OutgoingSearchResult,
    newQuantity: number,
    currentQuantity: number = 1
): OutgoingSearchResult {
    if (!searchResult.nutritionPreview || currentQuantity <= 0) {
        return searchResult;
    }

    const multiplier = newQuantity / currentQuantity;

    return {
        ...searchResult,
        nutritionPreview: {
            ...searchResult.nutritionPreview,
            calories: Math.round(
                (searchResult.nutritionPreview.calories || 0) * multiplier
            ),
            protein:
                Math.round(
                    (searchResult.nutritionPreview.protein || 0) *
                        multiplier *
                        10
                ) / 10,
            carbs:
                Math.round(
                    (searchResult.nutritionPreview.carbs || 0) * multiplier * 10
                ) / 10,
            fat:
                Math.round(
                    (searchResult.nutritionPreview.fat || 0) * multiplier * 10
                ) / 10,
            fiber:
                Math.round(
                    (searchResult.nutritionPreview.fiber || 0) * multiplier * 10
                ) / 10,
        },
    };
}

/**
 * Formats nutrition values for display
 */
export function formatNutritionValue(
    value: number | undefined,
    unit: string = "g"
): string {
    if (value === undefined || value === null) return "-";
    // Round to whole numbers for clean display (data remains precise internally)
    return `${Math.round(value)}${unit}`;
}

/**
 * Creates a summary string of macros for quick display
 */
export function getMacrosSummary(nutritionPreview: any): string {
    if (!nutritionPreview) return "No nutrition data";

    const calories = nutritionPreview.calories || 0;
    const protein = nutritionPreview.protein || 0;
    const carbs = nutritionPreview.carbs || 0;
    const fat = nutritionPreview.fat || 0;

    return `${calories} cal • ${protein}g protein • ${carbs}g carbs • ${fat}g fat`;
}

/**
 * Creates a summary string of macros with rounded values for display
 */
export function getRoundedMacrosSummary(nutritionPreview: any): string {
    if (!nutritionPreview) return "No nutrition data";

    const calories = Math.round(nutritionPreview.calories || 0);
    const protein = Math.round(nutritionPreview.protein || 0);
    const carbs = Math.round(nutritionPreview.carbs || 0);
    const fat = Math.round(nutritionPreview.fat || 0);

    return `${calories} cal • ${protein}g protein • ${carbs}g carbs • ${fat}g fat`;
}

/**
 * Gets effective quantity from a portion, considering suggestedQuantity
 */
export function getEffectiveQuantity(portion: MeasuredPortion | undefined): number {
    if (!portion) return 1;
    if (portion.suggestedQuantity && portion.suggestedQuantity.length > 0) {
        return portion.suggestedQuantity[0];
    }
    return portion.quantity || 1;
}

/**
 * Calculates nutrition values where spinnerValue is a multiplier for the portion weight
 * spinnerValue * portion.weight.value = total grams, then calculate nutrition for that
 */
export function calculateNutritionForPortionDirect(
    foodDetailResponse: PassioFoodDetailResponse,
    selectedPortion: MeasuredPortion,
    spinnerValue: number = 1
): {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
} {
    const ingredient = foodDetailResponse.results?.[0]?.ingredients?.[0];
    if (!ingredient || !ingredient.nutrients) {
        return { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 };
    }

    // Calculate total weight: spinner value * portion weight
    const totalWeightInGrams = spinnerValue * selectedPortion.weight.value;

    // API nutrients are per 100g
    const multiplier = totalWeightInGrams / 100;

    // Extract nutrients by shortName
    const getNutrientValue = (shortName: string): number => {
        const nutrient = ingredient.nutrients.find(
            (n) => n.nutrient.shortName === shortName
        );
        return nutrient?.amount || 0;
    };

    return {
        calories: Math.round(getNutrientValue("calories") * multiplier),
        protein: Math.round(getNutrientValue("protein") * multiplier * 10) / 10,
        carbs: Math.round(getNutrientValue("carbs") * multiplier * 10) / 10,
        fat: Math.round(getNutrientValue("fat") * multiplier * 10) / 10,
        fiber: Math.round(getNutrientValue("fiber") * multiplier * 10) / 10,
    };
}

/**
 * Extracts portion information from search result
 * Uses the actual portion data from nutritionPreview if available
 */
export function getPortionInfo(
    searchResult: OutgoingSearchResult,
    quantity: number = 1
): {
    quantity: number;
    unit: string;
    displayText: string;
} {
    // Use the actual portion info from nutritionPreview if available
    if (searchResult.nutritionPreview?.portion) {

        // Get the unit - prefer portion name if available, otherwise use weight unit
        const unit = searchResult.nutritionPreview.portion.name || 
                     searchResult.nutritionPreview.portion.weight?.unit || 
                     "serving";

        // The display quantity is always effectiveQuantity * user's quantity
        // effectiveQuantity is what the nutrition is based on (e.g., 100 for "100 grams")
        // quantity is the user's multiplier (e.g., 2 for "2 servings of 100 grams")
        const finalDisplayQuantity = quantity;

        // Smart pluralization - only for certain units
        let displayUnit = unit;
        if (finalDisplayQuantity !== 1 && unit === "serving") {
            displayUnit = "servings";
        }
        // Don't pluralize weight units (g, oz, etc.) or most portion names

        const displayText = `${finalDisplayQuantity} ${displayUnit}`;

        return {
            quantity: finalDisplayQuantity,
            unit: unit,
            displayText: displayText,
        };
    }

    // Default values if no portion info available
    const displayText = `${quantity} serving${quantity !== 1 ? "s" : ""}`;

    return {
        quantity: quantity,
        unit: "serving",
        displayText: displayText,
    };
}

/**
 * Creates a complete food summary with portion information and macros
 */
export function getCompleteFoodSummary(
    searchResult: OutgoingSearchResult,
    quantity: number = 1
): string {
    const portionInfo = getPortionInfo(searchResult, quantity);
    const macrosSummary = getRoundedMacrosSummary(
        searchResult.nutritionPreview
    );

    return `${portionInfo.displayText} • ${macrosSummary}`;
}

/**
 * Gets formatted portion text for display
 */
export function getPortionDisplayText(
    searchResult: OutgoingSearchResult,
    quantity: number = 1
): string {
    return getPortionInfo(searchResult, quantity).displayText;
}

/**
 * Calculates the total weight in grams for a given portion and quantity
 * Returns both the numeric value and a formatted display string
 */
export function getTotalWeightInGrams(
    portion: MeasuredPortion | null | undefined,
    quantity: number = 1
): {
    grams: number;
    displayText: string;
} {
    if (!portion || !portion.weight) {
        return {
            grams: 0,
            displayText: "",
        };
    }

    // Get the effective quantity (considering suggestedQuantity)
    // const effectiveQuantity = getEffectiveQuantity(portion);
    
    // Calculate total weight in grams
    const totalGrams = quantity * portion.weight.value;
    
    // Format for display - round to 1 decimal place if needed
    const roundedGrams = Math.round(totalGrams * 10) / 10;
    const displayText = roundedGrams % 1 === 0 
        ? `${Math.round(roundedGrams)}g`
        : `${roundedGrams}g`;

    return {
        grams: totalGrams,
        displayText: displayText,
    };
}
